import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Edit2, Trash2, Shield, Check, Loader, User, Lock, Save, X, ChevronDown, CheckCircle, AlertCircle } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchRoles,
  createRole,
  updateRole,
  deleteRole,
  fetchAdminUsers,
  createAdminUser,
  updateAdminUser,
  deleteAdminUser,
  fetchPermissions,
  assignRolesToUser,
} from '../redux/slices/adminSlice';
import toast from 'react-hot-toast';
import { PERMISSIONS, usePermissionHook, PERMISSION_DENIED_MESSAGE, permissionGuardClass } from '../utils/permissions';
import { ModalReporter } from '../context/ModalContext';
import { ShineBorder } from '../components/ui/shine-border';
import { getRoleDisplayLabel } from '../utils/roleDisplay';

const UserRoles = () => {
  const dispatch = useDispatch();
  const hasFetched = useRef(false);
  const hasFetchedRoles = useRef(false);
  const hasFetchedAdminUsers = useRef(false);
  const hasFetchedPermissions = useRef(false);

  // İzin kontrolleri
  const canReadRoles = usePermissionHook(PERMISSIONS.ROLES_READ);
  const canManageRoles = usePermissionHook(PERMISSIONS.ROLES_MANAGE);
  const canReadUsers = usePermissionHook(PERMISSIONS.USERS_READ);
  const canManageUsers = usePermissionHook(PERMISSIONS.USERS_MANAGE);

  // Redux state
  const {
    roles,
    rolesLoading,
    rolesError,
    adminUsers,
    adminUsersLoading,
    adminUsersError,
    permissions,
  } = useSelector((state) => state.admin);

  // Local state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddAdminModal, setShowAddAdminModal] = useState(false);
  const [showEditRoleModal, setShowEditRoleModal] = useState(false);
  const [showEditAdminModal, setShowEditAdminModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, type: null, id: null });
  const [isDeletingAdmin, setIsDeletingAdmin] = useState(false);

  // Herhangi bir modal açıkken arka plan (body) scroll'unu kilitle — sağ scrollbar
  // şeridi ve arka plan kayması nedeniyle oluşan beyaz alanları engeller.
  const isAnyModalOpen =
    showAddModal || showEditRoleModal || showAddAdminModal || showEditAdminModal || deleteConfirm.show;
  useEffect(() => {
    if (!isAnyModalOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isAnyModalOpen]);
  const [expandedRoles, setExpandedRoles] = useState(new Set()); // Genişletilmiş rol kartları
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false); // Rol dropdown açık/kapalı

  // Form state - Rol
  const [roleFormData, setRoleFormData] = useState({
    displayName: '',
    description: '',
    permissions: [],
  });

  // Form state - Admin User
  const [adminFormData, setAdminFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    roleIds: [],
    status: 'active',
  });

  // Roller için kullanıcı sayısını hesapla
  const roleUserCounts = useMemo(() => {
    const countsById = {};
    const countsByName = {};

    if (Array.isArray(adminUsers)) {
      adminUsers.forEach((user) => {
        if (!user || !Array.isArray(user.roles)) return;

        user.roles.forEach((role) => {
          if (!role) return;
          const roleId = role.id || role.roleId;
          const roleName = role.displayName || role.DisplayName;

          if (roleId) {
            countsById[roleId] = (countsById[roleId] || 0) + 1;
          }

          if (roleName) {
            countsByName[roleName] = (countsByName[roleName] || 0) + 1;
          }
        });
      });
    }

    return { byId: countsById, byName: countsByName };
  }, [adminUsers]);

  const getRoleUserCount = useCallback(
    (role) => {
      if (!role) return 0;
      const idCount = roleUserCounts.byId[role.id];
      const nameKey = role.displayName || role.DisplayName;
      const nameCount = nameKey ? roleUserCounts.byName[nameKey] : undefined;
      return idCount ?? nameCount ?? role.userCount ?? 0;
    },
    [roleUserCounts]
  );

  // ESC tuşu ile modal kapatma
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && deleteConfirm.show) {
        setDeleteConfirm({ show: false, type: null, id: null });
      }
    };

    if (deleteConfirm.show) {
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [deleteConfirm.show]);

  // İlk yüklemede verileri çek (sadece bir kez - her veri tipi için ayrı flag)
  useEffect(() => {
    // Roller çek (sadece bir kez)
    if (!hasFetchedRoles.current && roles.length === 0 && !rolesLoading) {
      hasFetchedRoles.current = true;
      dispatch(fetchRoles({ page: 1, limit: 100 }));
    }
  }, [dispatch, roles.length, rolesLoading]);

  useEffect(() => {
    // Admin kullanıcıları çek (sadece bir kez)
    if (!hasFetchedAdminUsers.current && adminUsers.length === 0 && !adminUsersLoading) {
      hasFetchedAdminUsers.current = true;
      dispatch(fetchAdminUsers({ page: 1, limit: 100 }));
    }
  }, [dispatch, adminUsers.length, adminUsersLoading]);

  useEffect(() => {
    // Permission'lar çek (sadece bir kez)
    if (!hasFetchedPermissions.current && permissions.length === 0) {
      hasFetchedPermissions.current = true;
      dispatch(fetchPermissions());
    }
  }, [dispatch, permissions.length]);

  // Rol oluşturma/güncelleme
  const handleRoleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (showEditRoleModal && roleFormData.id) {
        // Güncelle
        await dispatch(updateRole({
          roleId: roleFormData.id,
          displayName: roleFormData.displayName,
          description: roleFormData.description,
          permissions: roleFormData.permissions,
        })).unwrap();
        setShowEditRoleModal(false);
      } else {
        // Oluştur - Teknik adı otomatik oluştur
        const technicalName = generateTechnicalName(roleFormData.displayName);
        await dispatch(createRole({
          name: technicalName,
          displayName: roleFormData.displayName,
          description: roleFormData.description,
          permissions: roleFormData.permissions,
        })).unwrap();
        setShowAddModal(false);
      }

      resetRoleForm();
    } catch (error) {
      // Hata sessizce yok sayılıyor
    }
  };

  // Admin kullanıcı oluşturma/güncelleme
  const handleAdminSubmit = async (e) => {
    e.preventDefault();

    // Güncelleme için şifre zorunlu değil, sadece oluşturma için zorunlu
    if (showEditAdminModal && adminFormData.id) {
      // Güncelleme: Sadece fullName ve email kontrolü
      if (!adminFormData.fullName || !adminFormData.email) {
        toast.error('Lütfen ad soyad ve e-posta alanlarını doldurun');
        return;
      }
    } else {
      // Oluşturma: Tüm zorunlu alanlar
      if (!adminFormData.fullName || !adminFormData.email || !adminFormData.password) {
        toast.error('Lütfen tüm zorunlu alanları doldurun');
        return;
      }
      // Rol ataması zorunlu
      if (!adminFormData.roleIds || adminFormData.roleIds.length === 0) {
        toast.error('Lütfen bir rol seçin');
        return;
      }
    }

    try {
      if (showEditAdminModal && adminFormData.id) {
        // Güncelle
        const updateData = {
          userId: adminFormData.id,
          fullName: adminFormData.fullName,
          phone: adminFormData.phone,
          status: adminFormData.status,
        };

        // Şifre değiştiriliyorsa ekle
        if (adminFormData.password && adminFormData.password.trim() !== '') {
          updateData.password = adminFormData.password;
        }

        await dispatch(updateAdminUser(updateData)).unwrap();

        // Roller her zaman güncellenmeli (rol varsa veya yoksa bile)
        // Backend zaten mevcut rolleri kaldırıp yeni rolleri atayacak
        await dispatch(assignRolesToUser({
          userId: adminFormData.id,
          roleIds: adminFormData.roleIds || [],
        })).unwrap();

        toast.success('Kullanıcı başarıyla güncellendi');
        setShowEditAdminModal(false);
      } else {
        // Oluştur
        await dispatch(createAdminUser({
          fullName: adminFormData.fullName,
          email: adminFormData.email,
          phone: adminFormData.phone,
          password: adminFormData.password,
          roleIds: adminFormData.roleIds || [],
          status: adminFormData.status,
        })).unwrap();
        toast.success('Kullanıcı başarıyla oluşturuldu');
        setShowAddAdminModal(false);
      }

      // Listeyi yenile
      dispatch(fetchAdminUsers({ page: 1, limit: 100 }));
      resetAdminForm();
      setRoleDropdownOpen(false);
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || 'İşlem başarısız oldu';
      toast.error(errorMessage);
    }
  };

  // Rol silme
  const handleDeleteRole = async () => {
    try {
      await dispatch(deleteRole(deleteConfirm.id)).unwrap();
      // Redux reducer zaten state'ten rolü kaldırıyor, tekrar fetch etmeye gerek yok
      // dispatch(fetchRoles({ page: 1, limit: 100 })); // Gereksiz - Redux reducer zaten güncelliyor
      setDeleteConfirm({ show: false, type: null, id: null });
    } catch (error) {
      // Hata sessizce yok sayılıyor
    }
  };

  // Süper admin kontrolü
  const isSuperAdmin = (user) => {
    if (!user) return false;

    // Email kontrolü
    if (user.email && (
      user.email.includes('admin') ||
      user.email.includes('super') ||
      user.email === 'admin@sadakat.com' ||
      user.email === 'umitsahin@yenikoza.com.tr'
    )) {
      return true;
    }

    // FullName kontrolü
    if (user.fullName && (
      user.fullName.includes('ADMİN') ||
      user.fullName.includes('ADMIN') ||
      user.fullName.toLowerCase().includes('admin')
    )) {
      return true;
    }

    // isSuperAdmin kontrolü
    if (user.isSuperAdmin) {
      return true;
    }

    return false;
  };

  // Admin kullanıcı silme
  const handleDeleteAdmin = async () => {
    setIsDeletingAdmin(true);
    try {
      await dispatch(deleteAdminUser(deleteConfirm.id)).unwrap();
      
      setDeleteConfirm({ show: false, type: null, id: null });
      
      toast.success('Yönetici başarıyla silindi!', { 
        id: 'delete-admin-user',
        duration: 4000,
      });
      
      // Kullanıcı listesini yenile
      dispatch(fetchAdminUsers({ page: 1, limit: 100 }));
    } catch (error) {
      toast.error(error || 'Yönetici silinirken bir hata oluştu', { 
        id: 'delete-admin-user',
        duration: 4000
      });
    } finally {
      setIsDeletingAdmin(false);
    }
  };

  // Admin kullanıcı silme butonuna tıklandığında
  const handleDeleteAdminClick = (user) => {
    // Süper admin kontrolü
    if (isSuperAdmin(user)) {
      toast.error('Bu kullanıcı sistem tarafından korunmaktadır ve silinemez.', {
        duration: 4000,
        style: {
          background: '#fee2e2',
          color: '#991b1b',
          padding: '16px',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '500',
        },
        icon: '⚠️',
      });
      return;
    }

    // Normal kullanıcı ise modal'ı aç
    setDeleteConfirm({ show: true, type: 'admin', id: user.id });
  };

  // Rol düzenleme modalını aç
  const handleEditRole = (role) => {
    setRoleFormData({
      id: role.id,
      displayName: role.displayName || '',
      description: role.description || '',
      permissions: Array.isArray(role.permissions) ? role.permissions : [],
    });
    setShowEditRoleModal(true);
  };

  // Admin düzenleme modalını aç
  const handleEditAdmin = (user) => {
    setAdminFormData({
      id: user.id,
      fullName: user.fullName || user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      password: '', // Şifre gösterilmez
      roleIds: user.roles?.map(r => r.id) || [],
      status: user.status || 'active',
    });
    setRoleDropdownOpen(false);
    setShowEditAdminModal(true);
  };

  const selectedRole = roles.find((role) => role.id === adminFormData.roleIds[0]);

  // Form sıfırlama
  const resetRoleForm = () => {
    setRoleFormData({
      displayName: '',
      description: '',
      permissions: [],
    });
  };

  const resetAdminForm = () => {
    setAdminFormData({
      fullName: '',
      email: '',
      phone: '',
      password: '',
      roleIds: [],
      status: 'active',
    });
    setRoleDropdownOpen(false);
  };

  // Permission seçimi (basit checkbox mantığı - gerçek permission listesi için optimize edilebilir)
  const handlePermissionToggle = (permissionKey) => {
    setRoleFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(permissionKey)
        ? prev.permissions.filter(p => p !== permissionKey)
        : [...prev.permissions, permissionKey],
    }));
  };

  // Tarih formatla - Date objesi veya string kabul eder
  const formatDate = (dateInput) => {
    if (!dateInput) return '-';
    try {
      let date;

      // Eğer Date objesi ise direkt kullan
      if (dateInput instanceof Date) {
        date = dateInput;
      } else {
        // String ise Date'e çevir
        date = new Date(dateInput);
      }

      // Geçerli bir tarih mi kontrol et
      if (isNaN(date.getTime())) {
        return '-';
      }

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}.${month}.${year}`;
    } catch {
      return '-';
    }
  };

  // Hata mesajını string'e çevir
  const getErrorMessage = (error) => {
    if (!error) return '';
    if (typeof error === 'string') return error;
    if (error.message) return error.message;
    if (error.error) return error.error;
    if (typeof error === 'object') {
      return JSON.stringify(error);
    }
    return 'Bilinmeyen hata';
  };

  const permissionDictionary = {
    'campaigns.read': 'Kampanyaları görüntüleme',
    'campaigns.manage': 'Kampanyaları oluşturma ve düzenleme',
  };

  // Permission key'den Türkçe açıklamayı al
  const getPermissionDescription = (permissionKey) => {
    if (!permissionKey) {
      return '-';
    }
    const permission = permissions?.find?.((p) => (p.key || p.id || p.name) === permissionKey);
    return permission?.description || permission?.name || permissionDictionary[permissionKey] || permissionKey;
  };

  // Display name'den teknik ad oluştur
  const generateTechnicalName = (displayName) => {
    if (!displayName) return '';

    return displayName
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Türkçe karakterleri kaldır
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ş/g, 's')
      .replace(/ı/g, 'i')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c')
      .replace(/[^a-z0-9\s]/g, '') // Özel karakterleri kaldır
      .trim()
      .replace(/\s+/g, '_'); // Boşlukları alt çizgiye çevir
  };

  const anyModalOpen =
    showAddModal || showEditRoleModal || showAddAdminModal || showEditAdminModal || deleteConfirm.show;

  return (
    <div className="space-y-4">
      <ModalReporter open={anyModalOpen} />
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm p-4">
        <h1 className="text-lg font-semibold text-gray-900">Yönetici Rolleri ve Yetkileri</h1>
        <p className="text-gray-500 mt-1">Admin paneli kullanıcılarının rollerini ve erişim yetkilerini tanımlayın</p>
      </div>

      {/* Add Role Button */}
      <div className="flex justify-end">
        <button
          onClick={() => {
            if (!canManageRoles) {
              toast.error(PERMISSION_DENIED_MESSAGE);
              return;
            }
            resetRoleForm();
            setShowAddModal(true);
          }}
          className={`flex items-center gap-2 px-6 py-3 bg-blue-500 text-white rounded-xl transition-colors font-medium ${permissionGuardClass(canManageRoles)} ${canManageRoles ? 'hover:bg-blue-600' : ''}`}
          aria-disabled={!canManageRoles}
        >
          <Plus className="w-5 h-5" /> Yeni Rol Ekle
        </button>
      </div>

      {/* Roles Grid */}
      {rolesLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader className="animate-spin text-2xl text-blue-500" />
        </div>
      ) : rolesError ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-red-800">Hata: {getErrorMessage(rolesError)}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
          {roles.map((role) => (
            <ShineBorder
              key={role.id}
              borderWidth={2}
              duration={4}
              gradient="from-blue-500 via-red-500 to-teal-400"
              className="w-full h-full"
            >
              <div className="h-full flex flex-col bg-white rounded-2xl shadow-sm p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                    <Shield className="text-2xl text-blue-500" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{getRoleDisplayLabel(role)}</h3>
                    <p className="text-sm text-gray-500">{getRoleUserCount(role)} kullanıcı</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      if (!canManageRoles) {
                        toast.error(PERMISSION_DENIED_MESSAGE);
                        return;
                      }
                      handleEditRole(role);
                    }}
                    className={`p-2 text-gray-600 rounded-lg transition-colors ${permissionGuardClass(canManageRoles)} ${canManageRoles ? 'hover:bg-gray-100' : ''}`}
                    aria-disabled={!canManageRoles}
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      if (!canManageRoles) {
                        toast.error(PERMISSION_DENIED_MESSAGE);
                        return;
                      }
                      setDeleteConfirm({ show: true, type: 'role', id: role.id });
                    }}
                    className={`p-2 text-red-600 rounded-lg transition-colors ${permissionGuardClass(canManageRoles)} ${canManageRoles ? 'hover:bg-red-50' : ''}`}
                    aria-disabled={!canManageRoles}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-4">{role.description || 'Açıklama yok'}</p>

              <div className="space-y-2 mb-4 flex-grow">
                {role.permissions && role.permissions.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {(() => {
                      const isExpanded = expandedRoles.has(role.id);
                      const permissionsToShow = isExpanded ? role.permissions : role.permissions.slice(0, 5);

                      return (
                        <>
                          {permissionsToShow.map((perm, idx) => (
                            <span key={idx} className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">
                              {getPermissionDescription(perm)}
                            </span>
                          ))}
                          {role.permissions.length > 5 && (
                            <button
                              type="button"
                              onClick={() => {
                                setExpandedRoles(prev => {
                                  const newSet = new Set(prev);
                                  if (isExpanded) {
                                    newSet.delete(role.id);
                                  } else {
                                    newSet.add(role.id);
                                  }
                                  return newSet;
                                });
                              }}
                              className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs hover:bg-gray-200 transition-colors cursor-pointer"
                            >
                              {isExpanded ? 'Daha az göster' : `+${role.permissions.length - 5} daha`}
                            </button>
                          )}
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400">Yetki tanımlanmamış</p>
                )}
              </div>

              <div className="pt-4 border-t border-gray-200 mt-auto">
                <p className="text-xs text-gray-500">
                  Oluşturulma: {formatDate(role.createdAt || role.createdDate)}
                </p>
              </div>
              </div>
            </ShineBorder>
          ))}
          {roles.length === 0 && (
            <div className="col-span-2 text-center py-12 text-gray-500">
              Henüz rol tanımlanmamış
            </div>
          )}
        </div>
      )}

      {/* Admin Users Table */}
      <div className="bg-white rounded-2xl shadow-sm p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Admin Paneli Kullanıcıları</h2>
          <button
            onClick={() => {
              if (!canManageUsers) {
                toast.error(PERMISSION_DENIED_MESSAGE);
                return;
              }
              resetAdminForm();
              setRoleDropdownOpen(false);
              setShowAddAdminModal(true);
            }}
            className={`flex items-center gap-2 px-6 py-3 bg-green-500 text-white rounded-xl transition-colors font-medium ${permissionGuardClass(canManageUsers)} ${canManageUsers ? 'hover:bg-green-600' : ''}`}
            aria-disabled={!canManageUsers}
          >
            <Plus className="w-5 h-5" /> Yönetici Ekle
          </button>
        </div>

        {adminUsersLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader className="animate-spin text-2xl text-green-500" />
          </div>
        ) : adminUsersError ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="text-red-800">Hata: {getErrorMessage(adminUsersError)}</p>
          </div>
        ) : (
          <div className="overflow-x-auto ">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Ad Soyad
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    E-posta
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Roller
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Durum
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {adminUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      {user.fullName || user.name || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {user.email || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex flex-wrap gap-1">
                        {user.roles && user.roles.length > 0 ? (
                          user.roles.map((role) => (
                            <span
                              key={role.id}
                              className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold"
                            >
                              {getRoleDisplayLabel(role)}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-gray-400">Rol atanmamış</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${user.status === 'active' || !user.status
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                          }`}
                      >
                        {user.status === 'active' || !user.status ? 'Aktif' : 'Pasif'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            if (!canManageUsers) {
                              toast.error(PERMISSION_DENIED_MESSAGE);
                              return;
                            }
                            handleEditAdmin(user);
                          }}
                          className={`p-2 text-gray-600 rounded-lg transition-colors ${permissionGuardClass(canManageUsers)} ${canManageUsers ? 'hover:bg-gray-100' : ''}`}
                          aria-disabled={!canManageUsers}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            if (!canManageUsers) {
                              toast.error(PERMISSION_DENIED_MESSAGE);
                              return;
                            }
                            handleDeleteAdminClick(user);
                          }}
                          className={`p-2 text-red-600 rounded-lg transition-colors ${permissionGuardClass(canManageUsers)} ${canManageUsers ? 'hover:bg-red-50' : ''}`}
                          aria-disabled={!canManageUsers}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {adminUsers.length === 0 && (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                      Henüz yönetici kullanıcı eklenmemiş
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Role Modal */}
      {showAddModal && createPortal((
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-xl p-4 max-w-2xl w-full max-h-[88vh] overflow-y-auto">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Yeni Rol Oluştur</h2>

            <form onSubmit={handleRoleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Görünen Ad <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={roleFormData.displayName}
                    onChange={(e) => setRoleFormData({ ...roleFormData, displayName: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Örn: Kampanya Yöneticisi"
                  />
                  <p className="text-xs text-gray-500 mt-1">Teknik ad otomatik olarak oluşturulacak</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Açıklama</label>
                  <textarea
                    rows="3"
                    value={roleFormData.description}
                    onChange={(e) => setRoleFormData({ ...roleFormData, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Bu rolün yetkilerini açıklayın"
                  />
                </div>

                {permissions.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-4">Erişim Yetkileri</label>
                    <div className="space-y-2 max-h-60 overflow-y-auto border border-gray-200 rounded-xl p-4">
                      {permissions.map((permission) => (
                        <label key={permission.id || permission.key} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={roleFormData.permissions.includes(permission.key || permission.id)}
                            onChange={() => handlePermissionToggle(permission.key || permission.id)}
                            className="w-4 h-4 text-blue-500 rounded"
                          />
                          <span className="text-sm text-gray-700">
                            {permission.description || permission.name || permission.key}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-3 mt-5">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    resetRoleForm();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={rolesLoading}
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {rolesLoading ? 'Oluşturuluyor...' : 'Rolü Oluştur'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ), document.body)}

      {/* Edit Role Modal */}
      {showEditRoleModal && createPortal((
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-xl p-4 max-w-2xl w-full max-h-[88vh] overflow-y-auto">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Rol Düzenle</h2>

            <form onSubmit={handleRoleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Görünen Ad</label>
                  <input
                    type="text"
                    required
                    value={roleFormData.displayName}
                    onChange={(e) => setRoleFormData({ ...roleFormData, displayName: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Açıklama</label>
                  <textarea
                    rows="3"
                    value={roleFormData.description}
                    onChange={(e) => setRoleFormData({ ...roleFormData, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {permissions.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-4">Erişim Yetkileri</label>
                    <div className="space-y-2 max-h-60 overflow-y-auto border border-gray-200 rounded-xl p-4">
                      {permissions.map((permission) => (
                        <label key={permission.id || permission.key} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={roleFormData.permissions.includes(permission.key || permission.id)}
                            onChange={() => handlePermissionToggle(permission.key || permission.id)}
                            className="w-4 h-4 text-blue-500 rounded"
                          />
                          <span className="text-sm text-gray-700">
                            {permission.description || permission.name || permission.key}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-3 mt-5">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditRoleModal(false);
                    resetRoleForm();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={rolesLoading}
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {rolesLoading ? 'Güncelleniyor...' : 'Değişiklikleri Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ), document.body)}

      {/* Add Admin Modal */}
      {showAddAdminModal && createPortal((
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-xl p-4 max-w-2xl w-full max-h-[88vh] overflow-y-auto">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Yeni Yönetici Ekle</h2>

            <form onSubmit={handleAdminSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ad Soyad <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={adminFormData.fullName}
                    onChange={(e) => setAdminFormData({ ...adminFormData, fullName: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Örn: Mehmet Yılmaz"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    E-posta <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    required
                    value={adminFormData.email}
                    onChange={(e) => setAdminFormData({ ...adminFormData, email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="ornek@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Telefon</label>
                  <input
                    type="tel"
                    value={adminFormData.phone}
                    onChange={(e) => setAdminFormData({ ...adminFormData, phone: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="05XX XXX XX XX"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Şifre <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    required
                    value={adminFormData.password}
                    onChange={(e) => setAdminFormData({ ...adminFormData, password: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Minimum 8 karakter"
                  />
                </div>

                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rol <span className="text-red-500">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setRoleDropdownOpen(!roleDropdownOpen)}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 bg-white text-left flex items-center justify-between"
                  >
                    <span className="text-gray-700">
                      {selectedRole
                        ? getRoleDisplayLabel(selectedRole)
                        : 'Rol seçin'}
                    </span>
                    <svg
                      className={`w-5 h-5 text-gray-400 transition-transform ${roleDropdownOpen ? 'transform rotate-180' : ''}`}
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {roleDropdownOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setRoleDropdownOpen(false)}
                      />
                      <div className="absolute z-20 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                        {roles.length === 0 ? (
                          <div className="px-4 py-3 text-sm text-gray-500 text-center">
                            Henüz rol tanımlanmamış
                          </div>
                        ) : (
                          roles.map((role) => {
                            const isSelected = adminFormData.roleIds[0] === role.id;
                            return (
                              <button
                                type="button"
                                key={role.id}
                                onClick={() => {
                                  setAdminFormData({ ...adminFormData, roleIds: [role.id] });
                                  setRoleDropdownOpen(false);
                                }}
                                className={`w-full flex items-center justify-between px-4 py-3 border-b border-gray-100 last:border-b-0 transition-colors ${isSelected ? 'bg-green-50 text-green-700' : 'hover:bg-gray-50 text-gray-700'
                                  }`}
                              >
                                <span className="text-sm">{getRoleDisplayLabel(role)}</span>
                                {isSelected && <Check className="w-4 h-4 text-green-600" />}
                              </button>
                            );
                          })
                        )}
                      </div>
                    </>
                  )}

                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Durum</label>
                  <select
                    value={adminFormData.status}
                    onChange={(e) => setAdminFormData({ ...adminFormData, status: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="active">Aktif</option>
                    <option value="suspended">Pasif</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-3 mt-5">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddAdminModal(false);
                    resetAdminForm();
                    setRoleDropdownOpen(false);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={adminUsersLoading}
                  className="flex-1 px-4 py-2 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {adminUsersLoading ? 'Oluşturuluyor...' : 'Yönetici Ekle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ), document.body)}

      {/* Edit Admin Modal */}
      {showEditAdminModal && createPortal((
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-xl p-4 max-w-2xl w-full max-h-[88vh] overflow-y-auto">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Yönetici Düzenle</h2>

            <form onSubmit={handleAdminSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Ad Soyad</label>
                  <input
                    type="text"
                    required
                    value={adminFormData.fullName}
                    onChange={(e) => setAdminFormData({ ...adminFormData, fullName: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">E-posta</label>
                  <input
                    type="email"
                    disabled
                    value={adminFormData.email}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl bg-gray-50 text-gray-500 cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-500 mt-1">E-posta adresi değiştirilemez</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Telefon</label>
                  <input
                    type="tel"
                    value={adminFormData.phone}
                    onChange={(e) => setAdminFormData({ ...adminFormData, phone: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Şifre (Değiştirmek için yeni şifre girin)</label>
                  <input
                    type="password"
                    value={adminFormData.password}
                    onChange={(e) => setAdminFormData({ ...adminFormData, password: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Boş bırakırsanız şifre değişmez"
                  />
                </div>

                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Rol</label>
                  <button
                    type="button"
                    onClick={() => setRoleDropdownOpen(!roleDropdownOpen)}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 bg-white text-left flex items-center justify-between"
                  >
                    <span className="text-gray-700">
                      {selectedRole
                        ? getRoleDisplayLabel(selectedRole)
                        : 'Rol seçin'}
                    </span>
                    <svg
                      className={`w-5 h-5 text-gray-400 transition-transform ${roleDropdownOpen ? 'transform rotate-180' : ''}`}
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {roleDropdownOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setRoleDropdownOpen(false)}
                      />
                      <div className="absolute z-20 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                        {roles.length === 0 ? (
                          <div className="px-4 py-3 text-sm text-gray-500 text-center">
                            Henüz rol tanımlanmamış
                          </div>
                        ) : (
                          roles.map((role) => {
                            const isSelected = adminFormData.roleIds[0] === role.id;
                            return (
                              <button
                                type="button"
                                key={role.id}
                                onClick={() => {
                                  setAdminFormData({ ...adminFormData, roleIds: [role.id] });
                                  setRoleDropdownOpen(false);
                                }}
                                className={`w-full flex items-center justify-between px-4 py-3 border-b border-gray-100 last:border-b-0 transition-colors ${isSelected ? 'bg-green-50 text-green-700' : 'hover:bg-gray-50 text-gray-700'
                                  }`}
                              >
                                <span className="text-sm">{getRoleDisplayLabel(role)}</span>
                                {isSelected && <Check className="w-4 h-4 text-green-600" />}
                              </button>
                            );
                          })
                        )}
                      </div>
                    </>
                  )}

                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Durum</label>
                  <select
                    value={adminFormData.status}
                    onChange={(e) => setAdminFormData({ ...adminFormData, status: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="active">Aktif</option>
                    <option value="suspended">Pasif</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-3 mt-5">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditAdminModal(false);
                    resetAdminForm();
                    setRoleDropdownOpen(false);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={adminUsersLoading}
                  className="flex-1 px-4 py-2 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {adminUsersLoading ? 'Güncelleniyor...' : 'Değişiklikleri Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ), document.body)}

      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && createPortal((
        <div
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-3"
          onClick={(e) => {
            // Modal dışına tıklandığında kapat
            if (e.target === e.currentTarget) {
              setDeleteConfirm({ show: false, type: null, id: null });
            }
          }}
        >
          <div className="bg-white rounded-xl p-4 max-w-xl w-full">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {deleteConfirm.type === 'role' ? 'Rolü Sil' : 'Yöneticiyi Sil'}
            </h2>
            <p className="text-gray-600 mb-4">
              Bu işlem geri alınamaz. {deleteConfirm.type === 'role' ? 'Rolü' : 'Yöneticiyi'} silmek istediğinizden emin misiniz?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm({ show: false, type: null, id: null })}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors font-medium"
              >
                İptal
              </button>
              <button
                onClick={deleteConfirm.type === 'role' ? handleDeleteRole : handleDeleteAdmin}
                disabled={deleteConfirm.type === 'admin' && isDeletingAdmin}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {deleteConfirm.type === 'admin' && isDeletingAdmin ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Siliniyor...
                  </>
                ) : (
                  'Sil'
                )}
              </button>
            </div>
          </div>
        </div>
      ), document.body)}
    </div>
  );
};

export default UserRoles;
